import {
  setPlacemarkData,
  findAddressByCoords,
  findCoordsByAddress,
} from '../../../pages/address/DeliveryAddressPage/utils';
import { ymapsTypes } from '@/entities';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useRef, useCallback, useEffect, useState } from 'react';
import { createPlacemark } from '../../../pages/address/DeliveryAddressPage/utils';
import { formatCoordinatesForInput, parseCoordinatesFromInput, validateCoordinatesFormat } from '@/shared/lib';
import { OrderDeliveryState } from '@/app/store';
import { useChangePersonalInfoDelivery } from '@/features';

interface UseAddressViewDeliveryProps {
  actualAddress: string;
  changePointLable: (newLabel: string) => void;
  changeActualAddress: (newAddress: string) => void;
  changePointCoords: (newCoords: number[]) => void;
  setMapError: (error: string) => void;
  inputedCoords: string;
  changeInputedCoords: (coords: string) => void;
  delivery: OrderDeliveryState;
  isOrderAnotherPerson: boolean;
}

export const useAddressViewDelivery = ({
  actualAddress,
  changePointLable,
  changeActualAddress,
  changePointCoords,
  setMapError,
  inputedCoords,
  changeInputedCoords,
  delivery,
  isOrderAnotherPerson,
}: UseAddressViewDeliveryProps) => {
  const { t: tAddress } = useTranslation('address');
  const ymaps = useRef<typeof ymapsTypes.Map | undefined>(undefined);
  const placemarkRef = useRef<typeof ymapsTypes | undefined>(undefined);
  const mapRef = useRef<typeof ymapsTypes.Map | undefined>(undefined);
  const lastAddressRef = useRef<string>('');
  const [debouncedInputedCoords] = useDebounce(inputedCoords, 1000);
  const { areManualCoordinates } = delivery;
  const [changeFindButtonLoading, setChangeFindButtonLoading] = useState(true);

  const { onSetDeliveryAddressAndCoords, onSetDeliveryAddressComponents } =
    useChangePersonalInfoDelivery(isOrderAnotherPerson);

  const onLoadMap = (ympasInstance: typeof ymapsTypes) => {
    ymaps.current = ympasInstance;

    if (!ympasInstance) {
      setMapError(tAddress('map.mapLoadError'));
    }

    if (lastAddressRef.current === actualAddress || actualAddress === '' || !ymaps.current) return;
    lastAddressRef.current = actualAddress;
    findCorrectAddressAndCoords();
  };

  const updatePlacemark = (coords: number[], addressLine: string) => {
    if (placemarkRef.current) {
      placemarkRef.current.geometry.setCoordinates(coords);
    } else {
      placemarkRef.current = createPlacemark(coords, ymaps, tAddress('map.loading'));
      mapRef.current.geoObjects.add(placemarkRef.current);

      placemarkRef.current.events.add('dragend', function () {
        getNewAddressByCoords(placemarkRef.current.geometry.getCoordinates());
      });
    }

    setPlacemarkData(placemarkRef, addressLine, addressLine);
    changePointLable(addressLine);
  };

  const findCorrectAddressAndCoords = useCallback(async () => {
    setChangeFindButtonLoading(true);

    let coords;
    if (!areManualCoordinates) {
      coords = await findCoordsByAddress(actualAddress, ymaps, tAddress);
    } else {
      if (delivery.coords) {
        coords = [delivery.coords.lat, delivery.coords.lng];
      }
    }

    if (!coords || 'error' in coords) {
      setChangeFindButtonLoading(false);
      return;
    }

    const res = await findAddressByCoords(coords, ymaps, tAddress, areManualCoordinates);

    if ('addressComponents' in res) {
      onSetDeliveryAddressComponents(res.addressComponents);
    }

    if ('error' in res) {
      setChangeFindButtonLoading(false);
      return;
    }

    if (res.address !== actualAddress && res.success) {
      lastAddressRef.current = res.address;
      changeActualAddress(res.address);
    }

    updatePlacemark(coords, res.address);
    mapRef.current?.setCenter(coords);
    changePointCoords(coords);
    setChangeFindButtonLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualAddress, changeFindButtonLoading, changeActualAddress, ymaps, tAddress]);

  const getNewAddressByCoords = async (coords: [number, number]) => {
    setChangeFindButtonLoading(true);
    if (!placemarkRef.current) {
      placemarkRef.current = createPlacemark(coords, ymaps);
      mapRef.current.geoObjects.add(placemarkRef.current);
      placemarkRef.current.properties.set('iconCaption', 'loading..');

      placemarkRef.current.events.add('dragend', function () {
        getNewAddressByCoords(placemarkRef.current.geometry.getCoordinates());
      });
    } else {
      placemarkRef.current.geometry.setCoordinates(coords);
    }

    const res = await findAddressByCoords(coords, ymaps, tAddress, areManualCoordinates);

    if ('addressComponents' in res) {
      onSetDeliveryAddressComponents(res.addressComponents);
    }

    if ('error' in res) {
      setChangeFindButtonLoading(false);
      return;
    }

    if (res.success) {
      lastAddressRef.current = res.address;
      changeActualAddress(res.address);
      setPlacemarkData(placemarkRef, res.address, res.address);
      changePointLable(res.address);
      onSetDeliveryAddressAndCoords({ address: res.address, coordinates: { lat: coords[0], lng: coords[1] } });
    }

    if (areManualCoordinates) {
      const coordsText = `${coords[0]}, ${coords[1]}`;
      changeInputedCoords(coordsText);
      if (!res.success) {
        onSetDeliveryAddressAndCoords({ address: '', coordinates: { lat: coords[0], lng: coords[1] } });
        changeActualAddress('');
        setPlacemarkData(placemarkRef, coordsText, coordsText);
        changePointLable(coordsText);
      }
    } else {
      if (!res.success) {
        setPlacemarkData(placemarkRef, res.address, res.address);
        changePointLable(res.address);
      }
    }

    mapRef.current?.setCenter(coords);
    changePointCoords(coords);
    setChangeFindButtonLoading(false);
  };

  useEffect(() => {
    if (
      validateCoordinatesFormat(debouncedInputedCoords) &&
      debouncedInputedCoords !==
        formatCoordinatesForInput(
          delivery.coords
            ? {
                latitude: delivery.coords.lat,
                longitude: delivery.coords.lng,
              }
            : undefined,
        )
    ) {
      const coords = parseCoordinatesFromInput(debouncedInputedCoords);
      if (coords) getNewAddressByCoords([coords.latitude, coords.longitude]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInputedCoords]);

  useEffect(() => {
    if (lastAddressRef.current === actualAddress || actualAddress === '' || !ymaps.current) return;
    lastAddressRef.current = actualAddress;
    findCorrectAddressAndCoords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualAddress, ymaps.current]);

  const onMapClick = (e: typeof ymapsTypes.MapEvent) => {
    const coords = e.get('coords');
    getNewAddressByCoords(coords);
  };

  return { ymaps, placemarkRef, mapRef, lastAddressRef, onMapClick, onLoadMap, changeFindButtonLoading };
};
